import React, { useEffect, useState } from 'react';
import { useMatchMedia } from '@udemy/hooks';
import { formatNumber, useI18n } from '@udemy/i18n';
import { Button, Image } from '@udemy/react-core-components';
import { CardMediaImage, CardProgress, CardTitle, VerticalCard, Tag, RatingTag, TagList, CardDescription } from '@udemy/react-product-card-components';
import { CarouselWithFocusOrder } from '@udemy/react-structure-components';
import { useActiveSubscriptions } from '@udemy/subscription-graphql';
import { useGwgAcceleratorWithEnrollment } from '../../../contexts/gwg-enrollment-context';
import { getGwgAccelerator } from '../../../get-gwg-accelerators/gwg-accelerators-api';
import { useGwgCarouselCourses, useGwgCarouselCta, useGwgCarouselEnrollment } from './gwg-carousel.hooks';
import styles from "./gwg-carousel.module.css";
/**
 * GwgCarouselWrapper - A wrapper component that fetches accelerator data and renders GwgCarousel.
 *
 * This component handles data fetching internally, making it ideal for use cases where you only
 * have the accelerator slug and want the component to manage the data loading lifecycle.
 *
 * **IMPORTANT: Authentication Required**
 * This component requires the user to be authenticated. The underlying `getGwgAccelerator` API call
 * will fail with a 401 error if the user is not authenticated. Ensure proper authentication is in
 * place before using this component in production.
 *
 * **Usage:**
 * ```tsx
 * Simple usage with just a slug
 * <GwgCarouselWrapper slug="ai-professional-certificate" />
 *
 * The component will:
 * 1. Extract the locale from the i18n context (e.g., "en_US" -> "en")
 * 2. Fetch accelerator data using getGwgAccelerator(slug, locale)
 * 3. Automatically fetch subscription plans via hooks
 * 4. Render GwgCarousel once data is loaded
 * 5. Return null while loading or if fetch fails
 * ```
 *
 * **When to use:**
 * - You only have the accelerator slug (e.g., from a URL parameter or route)
 * - You want the component to handle data fetching automatically
 * - You don't need fine-grained control over the data loading process
 * - The user is authenticated (required for API calls)
 *
 * **When NOT to use:**
 * - You already have the accelerator data object
 * - You need to handle loading states or errors manually
 * - You want to use custom subscription plan data
 * - The user may not be authenticated (use GwgCarousel directly with mock data instead)
 *
 * **Props:**
 * @param slug - The accelerator slug (e.g., "ai-professional-certificate", "full-stack-web-developer")
 *               This should be the base slug without locale suffix (e.g., not "ai-professional-certificate-en")
 *
 * **Returns:**
 * - Renders `GwgCarousel` component once accelerator data is successfully fetched
 * - Returns `null` while loading or if the fetch fails
 *
 * **Locale Handling:**
 * The component automatically extracts the language code from the i18n locale context.
 * For example, "en_US" becomes "en", which is used to construct the full slug for the API call.
 * This ensures the correct localized accelerator data is fetched.
 *
 * @example
 * ```tsx
 * In a page component
 * function AcceleratorPage({slug}: {slug: string}) {
 *   return (
 *     <div>
 *       <GwgCarouselWrapper slug={slug} />
 *     </div>
 *   );
 * }
 * ```
 */
export const GwgCarouselWrapper = ({
  slug,
  onEnrollmentClick
}) => {
  var _useActiveSubscriptio;
  const [accelerator, setAccelerator] = useState(undefined);
  const {
    locale
  } = useI18n();
  // Extract the locale from the locale string (e.g. "en_US" -> "en")
  // This is used to fetch the accelerator in the proper slug format
  // (e.g. "ai-professional-certificate-en" instead of "ai-professional-certificate-en_US")
  const acceleratorLocale = locale.split('_')[0] || 'en';
  const currentlySubscribedPlan = (_useActiveSubscriptio = useActiveSubscriptions()) === null || _useActiveSubscriptio === void 0 ? void 0 : _useActiveSubscriptio[0];
  useEffect(() => {
    getGwgAccelerator(slug, acceleratorLocale).then(accelerator => {
      setAccelerator(accelerator);
    });
  }, [slug, acceleratorLocale]);
  return accelerator ? /*#__PURE__*/React.createElement(GwgCarousel, {
    accelerator: accelerator,
    currentlySubscribedPlan: currentlySubscribedPlan,
    onEnrollmentClick: onEnrollmentClick
  }) : null;
};
const GwgAcceleratorCard = ({
  accelerator,
  pathLandingUrl,
  cta
}) => {
  const {
    ninterpolate,
    locale
  } = useI18n();
  const stats = accelerator.stats;
  const avgRating = stats !== null && stats !== void 0 && stats.averageRating ? Number(stats.averageRating) : undefined;
  const totalReviews = stats !== null && stats !== void 0 && stats.numberOfRatings ? Number(stats.numberOfRatings) : undefined;
  const totalContentHours = stats !== null && stats !== void 0 && stats.durationInSeconds ? Math.round(Number(stats.durationInSeconds) / 60 / 60) : undefined;
  const tags = /*#__PURE__*/React.createElement(TagList, {
    tags: [avgRating && /*#__PURE__*/React.createElement(RatingTag, {
      key: "rating",
      rating: avgRating
    }), totalReviews && totalReviews > 0 && /*#__PURE__*/React.createElement(Tag, null, ninterpolate('%(count)s rating', '%(count)s ratings', totalReviews, {
      count: formatNumber(totalReviews, locale)
    })), totalContentHours && /*#__PURE__*/React.createElement(Tag, null, ninterpolate('%(count)s total hour', '%(count)s total hours', totalContentHours, {
      count: formatNumber(totalContentHours, locale)
    })), accelerator.curriculum.sections.length > 0 && /*#__PURE__*/React.createElement(Tag, null, ninterpolate('%(count)s course', '%(count)s courses', accelerator.curriculum.sections.length, {
      count: formatNumber(accelerator.curriculum.sections.length, locale)
    }))]
  });
  return /*#__PURE__*/React.createElement(VerticalCard, {
    className: styles['gwg-accelerator-card'],
    media: /*#__PURE__*/React.createElement("div", {
      className: styles['logo-container']
    }, /*#__PURE__*/React.createElement(Image, {
      src: "https://frontends.udemycdn.com/staticx/udemy/images/grow-with-google/google-logo.webp",
      alt: "Google Logo",
      width: 74,
      height: 24
    })),
    title: /*#__PURE__*/React.createElement(CardTitle, {
      renderLink: CardTitle.renderRouterLink({
        to: pathLandingUrl
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "ud-heading-xl"
    }, accelerator.career.name)),
    description: /*#__PURE__*/React.createElement(CardDescription, null, /*#__PURE__*/React.createElement("span", {
      className: "ud-text-sm"
    }, accelerator.career.description)),
    tags: tags,
    showSecondaryContentBorder: false,
    secondaryContent: cta
  });
};
const GwgCourseCard = ({
  course,
  index,
  totalCourses,
  courseHref,
  onClick
}) => {
  var _course$userEnrollmen;
  const {
    interpolate,
    gettext,
    locale
  } = useI18n();
  const durationMinutes = typeof course.durationInSeconds === 'number' ? course.durationInSeconds / 60 : null;
  const durationTag =
  // Already checked for number above, so we can just use durationMinutes directly.
  durationMinutes !== null ? durationMinutes < 60 ? interpolate(gettext('%(minutes)s mins'), {
    minutes: Math.round(durationMinutes)
  }, true) : interpolate(gettext('%(hours)s hours'), {
    hours: Number((durationMinutes / 60).toFixed(1))
  }, true) : null;
  const normalizedProgressPercent = typeof ((_course$userEnrollmen = course.userEnrollment) === null || _course$userEnrollmen === void 0 ? void 0 : _course$userEnrollmen.completionPercentage) === 'number' ? course.userEnrollment.completionPercentage : 0;
  const tags = /*#__PURE__*/React.createElement(TagList, {
    tags: [/*#__PURE__*/React.createElement(Tag, {
      key: 'course-index-tag'
    }, interpolate(gettext('Course %(courseIndex)s of %(totalCourses)s'), {
      courseIndex: formatNumber(index + 1, locale),
      totalCourses: formatNumber(totalCourses, locale)
    }, true)), durationTag && /*#__PURE__*/React.createElement(Tag, {
      key: "duration-tag"
    }, durationTag)]
  });
  return /*#__PURE__*/React.createElement("div", {
    className: styles['gwg-course-card-wrapper']
  }, /*#__PURE__*/React.createElement(VerticalCard, {
    className: styles['gwg-course-card'],
    media: /*#__PURE__*/React.createElement(CardMediaImage, {
      layout: "wide",
      src: course.images.height200,
      src2x: course.images.px750x422
    }),
    title: /*#__PURE__*/React.createElement(CardTitle, {
      renderLink: CardTitle.renderRouterLink({
        to: courseHref,
        onClick: event => onClick === null || onClick === void 0 ? void 0 : onClick(course, event)
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: `ud-heading-lg ${styles['gwg-course-card-title']}`
    }, course.title)),
    tags: tags,
    progress: normalizedProgressPercent > 0 ? /*#__PURE__*/React.createElement(CardProgress, {
      value: normalizedProgressPercent,
      label: gettext('Course progress')
    }) : null
  }));
};

/**
 * GwgCarousel - Displays a Grow with Google accelerator program with its courses in a carousel layout.
 *
 * This is the main presentation component that renders the accelerator card and course carousel.
 * It does not handle data fetching - you must provide the accelerator data and subscription plans.
 *
 * **Usage:**
 * ```tsx
 * // When you already have accelerator data
 * <GwgCarousel
 *   accelerator={acceleratorData}
 *   currentlySubscribedPlan={currentPlan}
 * />
 * ```
 *
 * **When to use:**
 * - You already have the `GwgAccelerator` data object (e.g., from a parent component or API call)
 * - You need fine-grained control over data loading, error handling, or loading states
 * - You want to use custom or mock subscription plan data
 * - You're using this in Storybook or tests with mock data
 * - The user may not be authenticated (you can pass mock data)
 *
 * **When NOT to use:**
 * - You only have the accelerator slug - use `GwgCarouselWrapper` instead
 * - You want automatic data fetching - use `GwgCarouselWrapper` instead
 *
 * **Component Structure:**
 * 1. **Accelerator Card** (always visible):
 *    - Displays Google logo, career name, description
 *    - Shows tags: rating, reviews, total hours, course count
 *    - Renders CTA button (enrollment for subscribers, "Learn more" link for non-subscribers)
 *
 * 2. **Course Carousel** (visible on medium+ screens only):
 *    - Horizontal scrollable carousel of course cards
 *    - Each course card shows: course image, title, course index, total hours
 *    - On mobile: allows scrolling
 *    - On desktop: shows pager buttons for navigation
 *
 * **Props:**
 * @param accelerator - The complete accelerator data object containing career info, curriculum, etc.
 * @param currentlySubscribedPlan - The user's current subscription plan (optional)
 *                                   Used to determine which CTA to show
 *
 * **Responsive Behavior:**
 * - Mobile (mobile-max): Course carousel allows scrolling, no pager buttons
 * - Desktop (md-min and above): Course carousel shows pager buttons, no scrolling
 * - Course carousel is hidden on mobile screens (only accelerator card is shown)
 *
 * **Styling:**
 * The component applies a background class based on the accelerator slug:
 * `styles[${accelerator.slug}-background]` - allows per-accelerator theming
 *
 * @example
 * ```tsx
 * // In a page component with pre-fetched data
 * function AcceleratorPage() {
 *   const [accelerator, setAccelerator] = useState<GwgAccelerator>();
 *   useEffect(() => {
 *     getGwgAccelerator('ai-professional-certificate', 'en').then(setAccelerator);
 *   }, []);
 *
 *   if (!accelerator) return <LoadingSpinner />;
 *
 *   return (
 *     <GwgCarousel
 *       accelerator={accelerator}
 *       currentlySubscribedPlan={currentPlan}
 *     />
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // In Storybook with mock data
 * <GwgCarousel
 *   accelerator={PLACEHOLDER_GWG_ACCELERATOR}
 *   currentlySubscribedPlan={mockCurrentPlan}
 * />
 * ```
 */
export const GwgCarousel = ({
  accelerator: acceleratorProp,
  currentlySubscribedPlan,
  onEnrollmentClick,
  onCourseCardClick
}) => {
  const isMobileMax = useMatchMedia('mobile-max');
  const isMdMin = useMatchMedia('md-min');

  // Merge client-side enrollment data into the accelerator
  const accelerator = useGwgAcceleratorWithEnrollment(acceleratorProp);
  const {
    courses,
    totalCourses,
    currentCourseIndex,
    currentCourse
  } = useGwgCarouselCourses(accelerator);
  const {
    isPathCompleted,
    isPathEnrolled,
    hasStartedAnyCourse
  } = useGwgCarouselEnrollment(accelerator, courses);
  const {
    ctaState,
    ctaCopyByState,
    ctaHrefByState,
    pathLandingUrl,
    courseCardHref
  } = useGwgCarouselCta({
    accelerator,
    currentlySubscribedPlan,
    totalCourses,
    currentCourseIndex,
    currentCourse,
    isPathEnrolled,
    isPathCompleted,
    hasStartedAnyCourse
  });
  const allowScroll = !!isMobileMax;
  const showPager = !allowScroll;
  return /*#__PURE__*/React.createElement("div", {
    className: styles[`${accelerator.slug}-background`]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['gwg-carousel']
  }, /*#__PURE__*/React.createElement(GwgAcceleratorCard, {
    accelerator: accelerator,
    pathLandingUrl: pathLandingUrl,
    cta: /*#__PURE__*/React.createElement("div", {
      className: styles['cta-container']
    }, /*#__PURE__*/React.createElement(Button, {
      componentClass: "a",
      href: ctaHrefByState[ctaState],
      size: "large",
      udStyle: "secondary",
      "data-purpose": "gwg-cta",
      className: styles['full-width-button'],
      onClick: onEnrollmentClick
    }, ctaCopyByState[ctaState]))
  }), isMdMin && /*#__PURE__*/React.createElement(CarouselWithFocusOrder, {
    className: styles['course-carousel'],
    gridMode: true,
    showPager: showPager,
    allowScroll: allowScroll
    // Type assertion: CarouselProps.pagerButtonSize is typed as 'medium'|'large'; runtime accepts 'small'.
    ,
    pagerButtonSize: 'small',
    pagerButtonClassName: styles['gwg-carousel-pager-button']
  }, accelerator.curriculum.sections.map((section, index) => /*#__PURE__*/React.createElement(GwgCourseCard, {
    key: section.buyable.id,
    course: section.buyable,
    index: index,
    totalCourses: totalCourses,
    courseHref: courseCardHref(section.buyable),
    onClick: onCourseCardClick
  })))));
};
//# sourceMappingURL=gwg-carousel.react-component.js.map